'use client'

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import Link from 'next/link'
import { ArrowUpRight } from '@carbon/icons-react'

import type { Feature } from './FeaturesHub'

/**
 * A keynote-style module slide, told with the page's own scroll. The section
 * claims a tall runway; the slide sticks to the viewport while the reader
 * scrolls through it, and their progress scrubs the module list through a
 * focus lens — whichever module crosses the centre line takes full ink and
 * puts its authored figure up in gradient type on the left.
 *
 * There is no inner scroll area and no autoplay: the ordinary page scroll is
 * the only input, so the wheel always does exactly what it does everywhere
 * else, and the slide releases naturally at either end of the runway.
 * Pointing at a row never repositions anything — hover only reveals a
 * "click to view" badge on that row.
 *
 * Every figure and caption is the module's own authored copy: the number
 * comes straight off `heroStat`, so no claim here is invented.
 *
 * One deliberate trick carried throughout: rows never animate `font-size`.
 * Each row is set at its focal size and only ever scales *down*, so the type
 * on the centre line renders crisp at its true size.
 */

// ─── Lens geometry ────────────────────────────────────────────────────────────
/** Focal (centre-line) font size, and the row pitch derived from it. */
const FOCAL_FONT = 'calc(clamp(16px, 1.7vw, 24px) * 1.68)'
const PITCH = 'calc(clamp(16px, 1.7vw, 24px) * 2.3847)'
/** Lens falloff as a multiple of the pitch — past this a row is fully receded. */
const FALLOFF_RATIO = 4.35484
/** Rows shrink from 1 down to this and no further. */
const SCALE_FLOOR = 0.4166667

// ─── Scroll choreography ──────────────────────────────────────────────────────
/** Page-scroll distance that advances the story by one module. */
const SCROLL_PER_ROW = 110

/* The pinned slide fills the viewport below the header, and the runway is
   derived from it so the two cannot drift apart.

   It must be a full viewport: while the slide is pinned, anything shorter
   leaves the remainder of the scroll runway visible as bare space underneath
   it. The fix for the hole under the section heading is to top-align the
   slide's CONTENTS (see the pane and grid below), not to shrink the pane — and
   the list is then sized to fill what the pane gives it, so there is no dead
   band at the bottom either. */
const PANE_H = 'calc(100svh - var(--header-h))'
const LIST_H = 'calc(100svh - var(--header-h) - 11vh)'
/** Per-frame approach of the lens toward the scroll position — a whisper of
 *  inertia so the scrub feels damped rather than bolted to the wheel. */
const SMOOTH = 0.22

const clamp01 = (x: number) => (x < 0 ? 0 : x > 1 ? 1 : x)
const smoothstep = (x: number) => x * x * (3 - 2 * x)
const lerp = (a: number, b: number, t: number) => a + (b - a) * t

// ─── Figure palettes ──────────────────────────────────────────────────────────
type RGB = [number, number, number]
/** Brand chroma, three stops per palette (core, mid, outer). Cycled per module
 *  and cross-faded, so the figure re-lights as the story advances. */
const PALETTES: [RGB, RGB, RGB][] = [
  [
    [55, 236, 176],
    [45, 212, 191],
    [125, 211, 252],
  ],
  [
    [125, 211, 252],
    [56, 189, 248],
    [129, 140, 248],
  ],
  [
    [45, 212, 191],
    [34, 211, 238],
    [96, 165, 250],
  ],
  [
    [167, 139, 250],
    [129, 140, 248],
    [45, 212, 191],
  ],
  [
    [110, 231, 183],
    [52, 211, 153],
    [56, 189, 248],
  ],
]

const mixRGB = (a: RGB, b: RGB, t: number): RGB => [
  lerp(a[0], b[0], t),
  lerp(a[1], b[1], t),
  lerp(a[2], b[2], t),
]
const rgba = (c: RGB, a: number) =>
  `rgba(${Math.round(c[0])},${Math.round(c[1])},${Math.round(c[2])},${a})`

/** Ink-coloured text with a radial wash clipped into it, blooming from below
 *  the baseline so the glyphs darken toward their cap height. */
const figureBackground = (p: [RGB, RGB, RGB]) =>
  `radial-gradient(95% 95% at 50% 110%, ${rgba(p[0], 1)} 0%, ${rgba(p[1], 0.669)} 34.13%, ` +
  `${rgba(p[2], 0.5)} 65.38%, ${rgba(p[2], 0)} 100%), ` +
  `linear-gradient(hsl(var(--foreground)), hsl(var(--foreground)))`

// ─── Figure sizing ────────────────────────────────────────────────────────────
/* Stat values run from "30s" to "Double-entry", and sizing them by character
   count put "100%" at twice the size of "Instant" — the figure appeared to
   change size at random as the story advanced.

   Instead every figure is measured and fitted to the same column width, so they
   share one optical size. The ceiling does the real work: any value that would
   fit at FIG_MAX is simply set there, which is most of them, so the whole set
   lands at one size and only the longest few step down — and they step down by
   just as much as they overrun, never in jumps. */
const FIG_MAX = 'clamp(3rem, 5.4vw, 5.5rem)'
/** Floor, as a fraction of the fitted maximum. Caps how far the longest value
 *  can diverge from the rest; below this it may overhang instead. */
const FIT_FLOOR = 0.68
/** Slot height as a multiple of FIG_MAX — line-height plus descender pad. */
const SLOT_RATIO = 1.24
/** Horizontal bleed on the figure, kept in sync with its padding below. */
const PAINT_PAD = 30
/** Size the measuring pass runs at; the fitted size scales off it. */
const FIT_REF = 100

/* Layout effects don't run on the server, and this component is server
   rendered — the plain effect avoids React's warning about that. */
const useIsoLayoutEffect =
  typeof window === 'undefined' ? useEffect : useLayoutEffect

// ─── Component ────────────────────────────────────────────────────────────────
export function ModuleSpotlight({
  features,
  basePath,
}: {
  features: Feature[]
  basePath: string
}) {
  const n = features.length

  const outerRef = useRef<HTMLDivElement>(null)
  const stickyRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const figureRef = useRef<HTMLSpanElement>(null)
  const figColRef = useRef<HTMLDivElement>(null)
  const slotRef = useRef<HTMLDivElement>(null)
  const rowRefs = useRef<(HTMLAnchorElement | null)[]>([])

  const [active, setActive] = useState(0)

  /* Derived above the effects rather than beside the markup, because the
     figure fitter below keys off `figure`. The empty-input guard has to wait
     until every hook has run, so these all tolerate a missing module. */
  const current = features[active] ?? features[0]
  const stat = current?.heroStat?.[0]
  const figure = stat?.value ?? String(active + 1).padStart(2, '0')

  /* rAF state — React state here would re-render 60× a second. */
  const idxRef = useRef(0)
  const activeRef = useRef(0)
  const paletteRef = useRef<[RGB, RGB, RGB]>(PALETTES[0]!)
  const reducedRef = useRef(false)

  useEffect(() => {
    const outer = outerRef.current
    const sticky = stickyRef.current
    const list = listRef.current
    if (!outer || !sticky || !list || n === 0) return

    reducedRef.current = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches
    const reduced = reducedRef.current

    /* Geometry cache. Everything the hot path needs is measured here — once on
       entry and again on resize/font-load — so neither scrollIdx() nor paint()
       ever reads layout. Layout reads per frame are what made this stutter:
       any style write elsewhere on the page dirties layout, and the next read
       then pays for a full synchronous reflow at animation rate. */
    const geom = { top: 0, runway: 1, listH: 0, pitch: 50 }
    const measureGeom = () => {
      geom.top = outer.getBoundingClientRect().top + window.scrollY
      geom.runway = Math.max(1, outer.offsetHeight - sticky.offsetHeight)
      geom.listH = list.clientHeight
      geom.pitch = rowRefs.current[0]?.offsetHeight || 50
    }

    /** Where the page scroll says the story should be, in fractional rows.
     *  Pure arithmetic on scrollY against the cache — no DOM reads. */
    const scrollIdx = () =>
      clamp01((window.scrollY - geom.top) / geom.runway) * (n - 1)

    const paint = () => {
      const idx = idxRef.current
      const h = geom.listH
      const cy = h / 2
      const pitch = geom.pitch
      const falloff = pitch * FALLOFF_RATIO

      let bestDist = Infinity
      let bestEl: HTMLAnchorElement | null = null
      let bestIdx = 0

      for (let i = 0; i < n; i++) {
        const el = rowRefs.current[i]
        if (!el) continue
        const off = (i - idx) * pitch
        const y = cy + off

        if (y < -pitch || y > h + pitch) {
          if (el.style.opacity !== '0') el.style.opacity = '0'
          el.style.pointerEvents = 'none'
          continue
        }

        const g = smoothstep(clamp01(Math.abs(off) / falloff))
        el.style.opacity = '1'
        el.style.pointerEvents = 'auto'
        el.style.transform = `translate3d(0, ${(y - pitch / 2).toFixed(2)}px, 0) scale(${lerp(1, SCALE_FLOOR, g).toFixed(4)})`
        el.style.color = 'hsl(var(--muted-foreground))'

        const dist = Math.abs(off)
        if (dist < bestDist) {
          bestDist = dist
          bestEl = el
          bestIdx = i
        }
      }

      /* Full ink goes to whichever row is nearest the centre line, so it
         flips exactly at the midpoint between two rows. */
      if (bestEl) bestEl.style.color = 'hsl(var(--foreground))'
      if (bestIdx !== activeRef.current) {
        activeRef.current = bestIdx
        setActive(bestIdx)
      }
    }

    measureGeom()

    if (reduced) {
      /* No inertia: track the scroll position exactly, repaint per event. */
      const onScroll = () => {
        idxRef.current = scrollIdx()
        paint()
      }
      const onResize = () => {
        measureGeom()
        onScroll()
      }
      onScroll()
      window.addEventListener('scroll', onScroll, { passive: true })
      window.addEventListener('resize', onResize)
      document.fonts?.ready.then(onResize).catch(() => {})
      return () => {
        window.removeEventListener('scroll', onScroll)
        window.removeEventListener('resize', onResize)
      }
    }

    idxRef.current = scrollIdx()
    paint()

    /* The loop runs only while there is distance left to close: each scroll
       event wakes it, and once the lens settles on the target it puts itself
       back to sleep. The previous version ran rAF continuously whenever the
       section was on screen — burning frames (and battery) while the reader
       just looked at it. */
    let raf = 0
    let running = false
    let inView = false

    const frame = () => {
      raf = 0
      if (!running) return
      const target = scrollIdx()
      const idx = idxRef.current
      if (Math.abs(target - idx) < 0.0005) {
        if (target !== idx) {
          idxRef.current = target
          paint()
        }
        running = false // settled — sleep until the next scroll event
        return
      }
      idxRef.current = idx + (target - idx) * SMOOTH
      paint()
      raf = requestAnimationFrame(frame)
    }

    const wake = () => {
      if (!inView || running || document.hidden) return
      running = true
      if (!raf) raf = requestAnimationFrame(frame)
    }
    const sleep = () => {
      running = false
      if (raf) cancelAnimationFrame(raf)
      raf = 0
    }

    const onScroll = () => wake()
    const onResize = () => {
      measureGeom()
      wake()
      /* wake() is a no-op when already settled on target, but the cached
         geometry just moved under the lens — force one repaint. */
      idxRef.current = scrollIdx()
      paint()
    }

    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onResize)
    document.fonts?.ready.then(onResize).catch(() => {})

    /* Entering the viewport re-measures — content above the section may have
       changed height since the last look — then arms the loop. */
    const io = new IntersectionObserver(
      ([e]) => {
        inView = !!e?.isIntersecting
        if (inView) {
          measureGeom()
          wake()
        } else {
          sleep()
        }
      },
      { threshold: 0 }
    )
    io.observe(outer)
    const onVis = () => (document.hidden ? sleep() : wake())
    document.addEventListener('visibilitychange', onVis)

    return () => {
      sleep()
      io.disconnect()
      document.removeEventListener('visibilitychange', onVis)
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onResize)
    }
  }, [n])

  /* Fit the figures to the column — all of them at once, on mount, resize and
     font load only. Fitting the current figure on each change was measured in
     a layout effect, and that read forced a synchronous reflow on every
     active-row flip; the scrub loop reads layout each frame, so every flip
     mid-scroll turned those per-frame reads into full reflows and the whole
     slide stuttered.

     Instead one hidden probe measures every value in a single batched pass
     (all writes, then all reads) and the results live in a map keyed by the
     figure string. The per-change path below is then a pure style write. */
  const fitMapRef = useRef<Map<string, number>>(new Map())

  useIsoLayoutEffect(() => {
    const measure = () => {
      const slot = slotRef.current
      const col = figColRef.current
      const map = fitMapRef.current
      if (!slot || !col) return

      const maxPx = slot.clientHeight / SLOT_RATIO
      /* The padding is a fixed pixel bleed, so it sits outside the fit ratio —
         only the glyphs scale. */
      const room = col.clientWidth - PAINT_PAD
      if (maxPx <= 0 || room <= 0) return

      const probe = document.createElement('div')
      probe.className = 'font-heading'
      probe.style.cssText =
        'position:absolute;left:-99999px;top:0;visibility:hidden;' +
        `pointer-events:none;font-size:${FIT_REF}px;font-weight:600;` +
        'letter-spacing:-0.055em;white-space:nowrap;'
      const values = features.map(
        (f, i) => f.heroStat?.[0]?.value ?? String(i + 1).padStart(2, '0')
      )
      const spans = values.map((v) => {
        const el = document.createElement('span')
        el.style.display = 'block'
        el.textContent = v
        probe.appendChild(el)
        return el
      })
      col.appendChild(probe)

      map.clear()
      spans.forEach((el, i) => {
        const glyphs = el.offsetWidth
        const fitted = glyphs > 0 ? (room / glyphs) * FIT_REF : maxPx
        map.set(
          values[i]!,
          Math.min(maxPx, Math.max(maxPx * FIT_FLOOR, fitted))
        )
      })
      probe.remove()

      const span = figureRef.current
      const px = map.get(figure)
      if (span && px) span.style.fontSize = `${px.toFixed(2)}px`
    }

    measure()
    window.addEventListener('resize', measure)
    document.fonts?.ready.then(measure).catch(() => {})
    return () => window.removeEventListener('resize', measure)
    /* `figure` is applied inside but deliberately not a dependency — changing
       modules must not re-measure; the write-only effect below handles it. */
  }, [features])

  /* Per-change sizing: a lone style write before paint. No layout reads here,
     so flipping the active row costs nothing the scrub loop can feel. */
  useIsoLayoutEffect(() => {
    const span = figureRef.current
    const px = fitMapRef.current.get(figure)
    if (span && px) span.style.fontSize = `${px.toFixed(2)}px`
  }, [figure])

  /* Cross-fade the figure's wash toward the active module's palette. */
  useEffect(() => {
    const fig = figureRef.current
    if (!fig) return
    const target = PALETTES[active % PALETTES.length]!
    if (reducedRef.current) {
      paletteRef.current = target
      fig.style.backgroundImage = figureBackground(target)
      return
    }
    let raf = 0
    const step = () => {
      const cur = paletteRef.current
      const next: [RGB, RGB, RGB] = [
        mixRGB(cur[0], target[0], 0.12),
        mixRGB(cur[1], target[1], 0.12),
        mixRGB(cur[2], target[2], 0.12),
      ]
      paletteRef.current = next
      fig.style.backgroundImage = figureBackground(next)
      /* 2/255 per channel is beneath perception; stopping there cuts the last
         dozen-odd frames of gradient-text repaint off every crossfade. */
      const done =
        Math.abs(next[0][0] - target[0][0]) +
          Math.abs(next[2][2] - target[2][2]) <
        2
      if (!done) raf = requestAnimationFrame(step)
    }
    raf = requestAnimationFrame(step)
    return () => cancelAnimationFrame(raf)
  }, [active])

  /* The 23 lens rows never change with `active` — the rAF loop owns their
     transform/opacity/colour directly. Memoising the elements means each
     row flip re-renders only the figure column: React sees identical element
     references for the rows and bails out of the whole list. */
  const rows = useMemo(
    () =>
      features.map((f, i) => (
        <Link
          key={f.slug}
          href={`${basePath}/${f.slug}`}
          ref={(el) => {
            rowRefs.current[i] = el
          }}
          className="group font-heading absolute left-0 top-0 flex items-center whitespace-nowrap font-medium leading-[1.05] outline-none"
          style={{
            /* Set at the focal size and only ever scaled down, so the
               type at the centre line renders at its true size. */
            fontSize: FOCAL_FONT,
            height: PITCH,
            transformOrigin: 'left center',
            willChange: 'transform',
            color: 'hsl(var(--muted-foreground))',
          }}
        >
          {f.title}
          {/* Hover/focus affordance — appears beside the row, never
              moves the row itself. */}
          <span
            aria-hidden
            className="ml-4 inline-flex shrink-0 -translate-x-1 items-center gap-1 rounded-full border border-border/70 bg-background/70 px-2.5 py-1 font-sans text-[11px] font-medium leading-none text-muted-foreground opacity-0 backdrop-blur transition-all duration-200 group-hover:translate-x-0 group-hover:opacity-100 group-focus-visible:translate-x-0 group-focus-visible:opacity-100"
          >
            Click to view
            <ArrowUpRight size={11} aria-hidden />
          </span>
        </Link>
      )),
    [features, basePath]
  )

  if (!current) return null

  return (
    /* The runway: one pane-height for the slide plus one story-beat per module.
       PANE_H drives both the sticky pane and this height — when the two
       disagree the leftover shows up as dead space, above the slide if the pane
       centres its contents and below it if the pane is taller than the runway
       assumes. */
    <div
      ref={outerRef}
      className="relative"
      style={{ height: `calc(${PANE_H} + ${(n - 1) * SCROLL_PER_ROW}px)` }}
    >
      {/* Sticks below the fixed header rather than under it, and is only as tall
          as its own contents: a full viewport here left a band of empty pane
          either above the slide (when centred) or below it (when top-aligned). */}
      <div
        ref={stickyRef}
        className="sticky top-[var(--header-h)] flex items-start pt-[4vh]"
        style={{ height: PANE_H }}
      >
        {/* Top-aligned: centring the short figure column against the tall module
              list pushed it to the middle of the viewport, leaving a hole under
              the section heading. */}
        <div className="grid w-full grid-cols-[minmax(0,36%)_minmax(0,64%)] items-start gap-6 xl:gap-10">
          {/* ── The figure ── */}
          <div ref={figColRef} className="min-w-0">
            <p className="text-[13px] font-medium leading-none tracking-[0.01em] text-muted-foreground">
              {current.category}
            </p>

            {/* Re-keyed per module so the figure re-enters instead of
                swapping. Figure and label both live in fixed-height slots —
                figures vary in size and labels in line count, and without the
                reserved space everything below would jump on each change. */}
            <div key={active} className="motion-safe:animate-spotlight-figure">
              <div
                ref={slotRef}
                className="flex items-end"
                style={{
                  marginTop: '0.35rem',
                  /* Also the single source of truth for the fitted ceiling —
                     the fitter reads this height back and divides it out, so
                     the slot and the type it holds cannot disagree. */
                  height: `calc(${FIG_MAX} * ${SLOT_RATIO})`,
                }}
              >
                <span
                  ref={figureRef}
                  className="font-heading block whitespace-nowrap"
                  style={{
                    /* Starting size only; the fitter overwrites this before
                       paint. Set to the ceiling so the server-rendered frame
                       and the fitted one agree for every short value. */
                    fontSize: FIG_MAX,
                    fontWeight: 600,
                    lineHeight: 1.08,
                    letterSpacing: '-0.055em',
                    marginLeft: `-${PAINT_PAD}px`,
                    /* Widens the paint box so the wash isn't clipped at the
                       glyph edges, and gives the ellipse room below the
                       baseline. The negative margin cancels the padding. */
                    padding: `0 ${PAINT_PAD}px 0.16em`,
                    backgroundImage: figureBackground(paletteRef.current),
                    WebkitBackgroundClip: 'text',
                    backgroundClip: 'text',
                    color: 'transparent',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  {figure}
                </span>
              </div>

              {/* Two-line slot regardless of how the label wraps. */}
              <p className="mt-1 min-h-[2.6em] max-w-[24ch] text-sm leading-snug text-muted-foreground sm:text-[15px]">
                {stat?.label}
              </p>
            </div>

            <Link
              href={`${basePath}/${current.slug}`}
              className="group mt-3 inline-flex items-center gap-1.5 text-[13px] font-medium text-emerald-600 dark:text-primary-active"
            >
              Open module
              <ArrowUpRight
                size={13}
                className="transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                aria-hidden
              />
            </Link>
          </div>

          {/* ── The lens list — scrubbed by the page scroll ── */}
          <div
            ref={listRef}
            style={{ height: LIST_H }}
            className="relative min-w-0 overflow-hidden [-webkit-mask-image:linear-gradient(to_bottom,transparent_0%,#000_16%,#000_84%,transparent_100%)] [mask-image:linear-gradient(to_bottom,transparent_0%,#000_16%,#000_84%,transparent_100%)]"
          >
            {rows}
          </div>
        </div>
      </div>
    </div>
  )
}

export default ModuleSpotlight
